import type { DetailedCalculationResult } from './types';

/**
 * Input values for rooms displaced calculation
 */
interface RoomsDisplacedInputs {
	capacity: number;
	newRoomsRequested: number;
	totalFcst: number;
}

/**
 * Calculation steps for rooms displaced calculation
 */
interface RoomsDisplacedSteps {
	totalRooms?: { value: number; description: string };
	roomsOverCapacity?: { value: number; description: string };
	roomsDisplaced?: { value: number; description: string };
}

/**
 * Calculate how many rooms are displaced by a group booking
 */
export const calculateRoomsDisplaced = (
	capacity: number,
	newRoomsRequested: number,
	totalFcst: number,
): DetailedCalculationResult<
	number,
	RoomsDisplacedInputs,
	RoomsDisplacedSteps
> => {
	// Early return case - no displacement needed
	if (capacity > newRoomsRequested + totalFcst) {
		return {
			value: 0,
			details: {
				inputValues: {
					capacity,
					newRoomsRequested,
					totalFcst,
				},
				reason: 'Capacity exceeds total rooms needed (requested + forecast)',
			},
		};
	}

	const totalRooms = newRoomsRequested + Math.min(totalFcst, capacity);
	const roomsOverCapacity = totalRooms - capacity;
	const roomsDisplaced = Math.min(roomsOverCapacity, capacity);

	return {
		value: roomsDisplaced,
		details: {
			inputValues: {
				capacity,
				newRoomsRequested,
				totalFcst,
			},
			calculations: {
				totalRooms: {
					value: totalRooms,
					description:
						'New rooms requested plus minimum of (forecast, capacity)',
				},
				roomsOverCapacity: {
					value: roomsOverCapacity,
					description: 'How many rooms exceed the capacity',
				},
				roomsDisplaced: {
					value: roomsDisplaced,
					description:
						'Final displacement (minimum of rooms over capacity and total capacity)',
				},
			},
		},
	};
};

/**
 * Calculate how many guests are walked (when current OTB is considered)
 */
export const calculateGuestsWalked = (
	capacity: number,
	newRoomsRequested: number,
	currentOtb: number,
) => {
	const totalRooms = newRoomsRequested + currentOtb;

	if (capacity > totalRooms) {
		return 0;
	}

	return totalRooms - capacity;
};

/**
 * Calculate net rooms added after accounting for displacement and wash factor
 */
export const calculateNetRoomsAdded = (
	capacity: number,
	newRoomsRequested: number,
	transientFcst: number,
	washFactor: number,
) => {
	const washedRooms = Math.round(newRoomsRequested * (washFactor / 100));
	const actualRooms = newRoomsRequested - washedRooms;
	if (capacity > actualRooms + transientFcst) {
		return actualRooms;
	}

	const roomsDisplaced = calculateRoomsDisplaced(
		capacity,
		newRoomsRequested,
		transientFcst,
	);

	return actualRooms - roomsDisplaced.value;
};
